import { Link } from 'react-router-dom'
import { Logo, Mark } from '../components/Logo'
import { cx } from '../components/ui'

/**
 * Hero illustration: a price band with the last few quotes plotted on it, and what happened to each.
 * Generic by design; nothing here is a product screen.
 */
const FEED: { who: string; deal: string; pos: number; band: 'ok' | 'sup' | 'head'; status: string; tone: 'ok' | 'wait' | 'no' }[] = [
  { who: 'MK', deal: 'Article 4471 · Account 2093', pos: 0.74, band: 'ok', status: 'Recorded', tone: 'ok' },
  { who: 'JS', deal: 'Article 1180 · Account 0412', pos: 0.36, band: 'sup', status: 'Approved by supervisor', tone: 'ok' },
  { who: 'AR', deal: 'Article 2207 · Account 3151', pos: 0.58, band: 'ok', status: 'Recorded', tone: 'ok' },
  { who: 'LB', deal: 'Article 3310 · Account 0779', pos: 0.14, band: 'head', status: 'Awaiting head of sales', tone: 'wait' },
  { who: 'MK', deal: 'Article 4471 · Account 1605', pos: 0.29, band: 'sup', status: 'Rejected, note left', tone: 'no' },
]
const FLOOR_AT = 0.22
const TARGET_AT = 0.46
const ASK_AT = 0.8
const DOT: Record<'ok' | 'sup' | 'head', string> = { ok: 'bg-emerald-500', sup: 'bg-amber-500', head: 'bg-rose-500' }
const STATUS: Record<'ok' | 'wait' | 'no', string> = {
  ok: 'bg-emerald-500/10 text-emerald-500 ring-emerald-500/20',
  wait: 'bg-amber-500/10 text-amber-500 ring-amber-500/20',
  no: 'bg-rose-500/10 text-rose-500 ring-rose-500/20',
}

function GovernanceIllustration() {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-6 shadow-panel">
      <div className="flex items-center justify-between">
        <div className="text-[13px] font-semibold text-sky-500">This week's quotes</div>
        <div className="text-[12px] text-slate-500">5 quotes · 2 escalated · 1 open</div>
      </div>

      {/* band strip */}
      <div className="mt-5">
        <div className="relative h-9">
          <div className="absolute inset-y-0 left-0 rounded-l-md bg-rose-500/10" style={{ width: `${FLOOR_AT * 100}%` }} />
          <div className="absolute inset-y-0 bg-amber-500/10" style={{ left: `${FLOOR_AT * 100}%`, width: `${(TARGET_AT - FLOOR_AT) * 100}%` }} />
          <div className="absolute inset-y-0 right-0 rounded-r-md bg-emerald-500/10" style={{ left: `${TARGET_AT * 100}%` }} />
          {[FLOOR_AT, TARGET_AT, ASK_AT].map((x) => (
            <div key={x} className="absolute inset-y-0 w-px bg-slate-600" style={{ left: `${x * 100}%` }} />
          ))}
          {FEED.map((q, i) => (
            <div
              key={i}
              className={cx('absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-panel', DOT[q.band])}
              style={{ left: `${q.pos * 100}%` }}
            />
          ))}
        </div>
        <div className="relative mt-1.5 h-4 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
          <span className="absolute -translate-x-1/2" style={{ left: `${FLOOR_AT * 100}%` }}>Floor</span>
          <span className="absolute -translate-x-1/2" style={{ left: `${TARGET_AT * 100}%` }}>Target</span>
          <span className="absolute -translate-x-1/2" style={{ left: `${ASK_AT * 100}%` }}>Ask</span>
        </div>
        <div className="mt-2 flex justify-between text-[11px] text-slate-500">
          <span>Head of sales</span>
          <span>Supervisor</span>
          <span>Rep's authority</span>
        </div>
      </div>

      {/* decision feed */}
      <ul className="mt-5 divide-y divide-slate-800 border-t border-slate-800">
        {FEED.map((q, i) => (
          <li key={i} className="flex items-center gap-3 py-2.5">
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-800 text-[11px] font-semibold text-slate-300">{q.who}</span>
            <span className={cx('h-2 w-2 shrink-0 rounded-full', DOT[q.band])} />
            <span className="min-w-0 flex-1 truncate text-[13px] text-slate-300">{q.deal}</span>
            <span className={cx('shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset', STATUS[q.tone])}>{q.status}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Section({ id, eyebrow, title, lead, children, className }: { id?: string; eyebrow: string; title: string; lead?: string; children?: React.ReactNode; className?: string }) {
  return (
    <section id={id} className={cx('py-20', className)}>
      <div className="mx-auto max-w-[1180px] px-8">
        <div className="label text-gold">{eyebrow}</div>
        <h2 className="serif mt-3 max-w-2xl text-[32px] font-bold leading-[1.2] text-sky-500">{title}</h2>
        {lead && <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-slate-400">{lead}</p>}
        {children}
      </div>
    </section>
  )
}

const EDGE = { navy: 'rgb(var(--accent))', gold: 'rgb(var(--gold))', green: 'rgb(var(--pos))', red: 'rgb(var(--neg))' } as const

function Feature({ title, body, edge = 'navy' }: { title: string; body: string; edge?: keyof typeof EDGE }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-6 shadow-panel" style={{ borderLeft: `3px solid ${EDGE[edge]}` }}>
      <div className="text-[16px] font-semibold" style={{ color: EDGE[edge] }}>{title}</div>
      <p className="mt-2 text-[14px] leading-relaxed text-slate-400">{body}</p>
    </div>
  )
}

const STEPS: [string, string][] = [
  ['Quote', 'The rep picks the article and the customer and sees the price to open with, the price to land on, and the price below which the deal is not theirs to make.'],
  ['Check', 'The rep enters the price the customer will actually get. The band is shown immediately: within authority, needs a supervisor, needs the head of sales.'],
  ['Escalate', 'Outside authority, the quote cannot simply be recorded. A reason is required and the request goes to the right approver, with the gap and the deal value attached.'],
  ['Decide', 'The approver approves, approves at a different price, or rejects, always with a note. The rep sees the decision where the quote was made.'],
  ['Close', 'The quote is closed as won or lost, at the final price. What was quoted, what was approved and what was signed sit side by side.'],
]

function Steps() {
  return (
    <ol className="mt-10 grid gap-4 md:grid-cols-5">
      {STEPS.map(([t, b], i) => (
        <li key={t} className="rounded-xl border border-slate-800 bg-slate-900/50 p-5 shadow-panel">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-sky-500 text-[13px] font-semibold tabular" style={{ color: 'rgb(var(--accent-contrast))' }}>
              {i + 1}
            </span>
            <span className="text-[15px] font-semibold text-sky-500">{t}</span>
          </div>
          <p className="mt-3 text-[13px] leading-relaxed text-slate-400">{b}</p>
        </li>
      ))}
    </ol>
  )
}

/**
 * How a price is built, in generic groups: a base, the drivers the model uses, the uplift, the target.
 * Abstract on purpose; the groups and their size stand for any driver-based model.
 */
const BUILD: { label: string; from: number; size: number; kind: 'base' | 'driver' | 'uplift' | 'total' }[] = [
  { label: 'Cost', from: 0, size: 58, kind: 'base' },
  { label: 'Market', from: 58, size: 7, kind: 'driver' },
  { label: 'Product', from: 65, size: 13, kind: 'driver' },
  { label: 'Customer', from: 78, size: 6, kind: 'driver' },
  { label: 'Deal terms', from: 84, size: -3, kind: 'driver' },
  { label: 'Uplift', from: 81, size: 5, kind: 'uplift' },
  { label: 'Target', from: 0, size: 86, kind: 'total' },
]
const BAR: Record<(typeof BUILD)[number]['kind'], string> = { base: 'bg-slate-700', driver: 'bg-sky-500', uplift: 'bg-gold', total: 'bg-sky-500' }

function EngineIllustration() {
  const top = 100
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-6 shadow-panel">
      <div className="flex items-center justify-between">
        <div className="text-[13px] font-semibold text-sky-500">How a price is built</div>
      </div>
      <div className="relative mt-6 flex h-[200px] items-stretch gap-2.5">
        {BUILD.map((b) => {
          const lo = b.size < 0 ? b.from + b.size : b.from
          return (
            <div key={b.label} className="relative flex-1">
              <div
                className={cx('absolute inset-x-0 rounded-[4px]', BAR[b.kind], b.kind === 'total' && 'opacity-90', b.size < 0 && 'opacity-50')}
                style={{ bottom: `${(lo / top) * 100}%`, height: `${(Math.abs(b.size) / top) * 100}%` }}
              />
            </div>
          )
        })}
      </div>
      <div className="mt-2 flex gap-2.5 border-t border-slate-800 pt-2">
        {BUILD.map((b) => (
          <div key={b.label} className={cx('flex-1 text-center text-[11px] leading-tight', b.kind === 'total' ? 'font-semibold text-sky-500' : 'text-slate-500')}>
            {b.label}
          </div>
        ))}
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        {['Relative (%) or absolute (€)', 'Rated 1–5 or labelled levels', 'Per article, customer, market or deal'].map((t) => (
          <span key={t} className="rounded-full bg-slate-800 px-3 py-1 text-[12px] text-slate-300">{t}</span>
        ))}
      </div>
    </div>
  )
}

export function Landing() {
  return (
    <div className="min-h-full">
      <header className="sticky top-0 z-20 border-b border-slate-800/80 bg-panel/90 backdrop-blur">
        <div className="mx-auto flex max-w-[1180px] items-center justify-between px-8 py-4">
          <Logo size={30} wordSize={17} />
          <nav className="hidden items-center gap-8 text-[14px] font-medium text-slate-400 md:flex">
            <a href="#governance" className="hover:text-slate-100">Governance</a>
            <a href="#process" className="hover:text-slate-100">How it works</a>
            <a href="#engine" className="hover:text-slate-100">Pricing engine</a>
            <a href="#roles" className="hover:text-slate-100">Who it is for</a>
            <a href="#audit" className="hover:text-slate-100">Audit</a>
          </nav>
          <div className="flex items-center gap-3">
            <a href="https://cognivance.org" className="hidden text-[14px] font-medium text-slate-400 hover:text-slate-100 sm:block">
              Talk to us
            </a>
            <Link to="/login" className="btn btn-primary">
              Sign in
            </Link>
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="mx-auto grid max-w-[1180px] items-center gap-14 px-8 pb-16 pt-24 lg:grid-cols-[1.05fr_1fr]">
        <div>
          <div className="label text-gold">Price governance for sales teams</div>
          <h1 className="serif mt-4 text-[50px] font-bold leading-[1.1] text-sky-500">
            Every quote in its band.
            <br />
            Every exception <span className="text-gold">on record</span>.
          </h1>
          <p className="mt-6 max-w-xl text-[18px] leading-relaxed text-slate-400">
            PricePilot gives every salesperson a clear price range for every deal, enforces who may go below it, and keeps a complete
            record of what was quoted, who approved it, and what was finally signed.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/login" className="btn btn-primary !h-11 !px-6 !text-[15px]">
              Sign in to PricePilot
            </Link>
            <a href="https://cognivance.org" className="btn !h-11 !px-6 !text-[15px]">
              Talk to us
            </a>
          </div>
          <div className="mt-10 grid max-w-xl grid-cols-3 gap-6 border-t border-slate-800 pt-6">
            {[
              ['Bands', 'a clear range per deal: where to open, where to land, where to stop'],
              ['Escalations', 'going below the band needs a reason and an approver, every time'],
              ['Audit', 'every quote, decision and outcome kept, with who, when and why'],
            ].map(([t, b]) => (
              <div key={t}>
                <div className="text-[15px] font-semibold text-sky-500">{t}</div>
                <div className="mt-1 text-[13px] leading-relaxed text-slate-500">{b}</div>
              </div>
            ))}
          </div>
        </div>
        <GovernanceIllustration />
      </section>

      <Section
        id="governance"
        eyebrow="Why"
        title="Discounts are given at the desk. Governance has to live there too."
        lead="Price policy usually exists as a slide deck and a memory. The sales rep decides in the moment, the exception goes by email, the approval is a reply that nobody can find a year later. The margin leaks one deal at a time, and nobody can say where. PricePilot puts the policy where the price is typed."
      >
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          <Feature edge="navy" title="A band for every deal" body="For each article and customer the rep sees three prices: where to open, where to land, and the floor. The range is set by the pricing team and is the same for every rep, every time." />
          <Feature edge="gold" title="Authority that is enforced" body="Inside the band the rep decides. Below it, a supervisor. Below the floor, the head of sales. The tool does not block anyone from asking; it makes sure the right person answers." />
          <Feature edge="green" title="Nothing off the record" body="Every quote, every escalation, every approval and every outcome is stored with who, when and why. Reporting on discount behaviour becomes a query, not an investigation." />
        </div>
      </Section>

      <Section
        id="process"
        eyebrow="How it works"
        title="Five steps, one place, no email."
        className="border-y border-slate-800 bg-panel"
      >
        <Steps />
      </Section>

      <Section
        id="engine"
        eyebrow="Pricing engine"
        title="Any driver-based price model."
        lead="Cost, market, product, customer, deal terms: PricePilot calculates Floor, Target and Ask from the drivers you choose, weighted the way you decide."
      >
        <div className="mt-10 grid items-start gap-6 lg:grid-cols-[1fr_1.1fr]">
          <EngineIllustration />
          <div className="grid gap-4 sm:grid-cols-2">
            <Feature edge="navy" title="Any driver, any level" body="Each driver adds a percentage or an amount per unit. Levels can be ratings from 1 to 5 or plain terms such as 3, 6 or 12 months." />
            <Feature edge="gold" title="Cost and market in view" body="Costs follow raw material and energy indices, including expected prices over the contract term. Targets stay anchored on the customer's current price." />
            <Feature edge="green" title="Changes previewed" body="New weights are loaded as a draft. Before it goes live, the draft shows which prices change; a published version can be rolled back." />
            <Feature edge="navy" title="Every price explained" body="Each target breaks down into what every driver added." />
          </div>
        </div>
      </Section>

      <Section
        id="roles"
        eyebrow="Who it is for"
        title="One product, three seats."
        lead="Each role sees one screen and one question."
      >
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          <Feature edge="navy" title="Sales" body="Pick an article and a customer. See the band, the account's history, and what was quoted last time. Enter a price, see where it stands, record the quote." />
          <Feature edge="gold" title="Sales management" body="A queue of escalations, oldest first: the gap to the band, the annualised deal value, the rep's reason. Approve, approve at a different price, or reject, always with a note." />
          <Feature edge="green" title="Pricing team" body="Set and adjust the bands and the approval rules. Preview which customers and articles are affected before a change goes live. Publish with a note, roll back with a reason." />
        </div>
      </Section>

      <Section
        id="audit"
        eyebrow="Audit"
        title="Every price explains itself, today and in three years."
        lead="Every quote keeps the rules it was checked against and the decision that was taken. Every change to the rules keeps the state before and after, by name and timestamp. When the auditor, the customer or the new head of sales asks how a price came about, the answer is one click away."
        className="border-y border-slate-800 bg-panel"
      >
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          <Feature edge="navy" title="Quote history" body="Per customer and per article: what was quoted, by whom, at which band, what was approved, and what was finally signed. Won and lost, with the final price." />
          <Feature edge="gold" title="Decision log" body="Every escalation with its reason, its approver, its decision and its note. Approval times, approval rates and discount granted, by team and by person." />
          <Feature edge="green" title="Rule changes" body="Every change to bands or approval rules is versioned. Nothing is overwritten, and a past quote is always read against the rules that applied on that day." />
        </div>
      </Section>

      <section className="mx-auto max-w-[1180px] px-8 py-20">
        <div className="flex flex-col items-start justify-between gap-6 rounded-xl border border-slate-800 bg-sky-500 p-10 shadow-panel md:flex-row md:items-center" style={{ color: 'rgb(var(--accent-contrast))' }}>
          <div>
            <div className="serif text-[28px] font-bold">Know what your sales team is giving away.</div>
            <div className="mt-2 text-[15px] opacity-80">Sign in if your company already runs PricePilot, or talk to us about bringing price governance to yours.</div>
          </div>
          <div className="flex gap-3">
            <Link to="/login" className="btn btn-gold !h-11 !px-6 !text-[15px]">
              Sign in
            </Link>
            <a href="https://cognivance.org" className="btn !h-11 !border-white/40 !bg-transparent !px-6 !text-[15px] !text-white">
              Talk to us
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-800">
        <div className="mx-auto flex max-w-[1180px] flex-wrap items-center justify-between gap-4 px-8 py-8 text-[13px] text-slate-500">
          <div className="flex items-center gap-2.5">
            <Mark size={22} />
            <span>
              PricePilot is a product of{' '}
              <a href="https://cognivance.org" className="font-medium text-slate-300 hover:text-slate-100">
                Cognivance
              </a>
            </span>
          </div>
          <div>© {new Date().getFullYear()} Cognivance · pricepilot.cognivance.org</div>
        </div>
      </footer>
    </div>
  )
}
